import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Add, Warning } from '@mui/icons-material';

// Reuse common constants and formatters
import { types } from '../../constants/constants';
import { parseAddress } from '../../utils/addressParser';
import { formatPhone } from '../../utils/formatters';
import { parseLead as parseLeadUtil } from '../../utils/leadParser';
import AssociationValidator from '../../utils/AssociationValidator';

// Minimal Association form used by the main Associations page.
import { AssociationsContext } from './context';

const LEGITIMATE_ASSOCIATION_PATTERNS = [
  /homeowners?\s+association/i,
  /\bhoa\b/i,
  /\bcoa\b/i,
  /condominium/i,
  /\bcondo\b/i,
  /estates?/i,
  /community/i,
  /village/i,
  /townhomes?/i,
  /apartments?/i,
  /management/i,
  /llc/i,
  /inc/i,
  /corp/i,
];

export default function AssociationForm({ saveAssociationToDb }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [_saving, setSaving] = useState(false);
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [quickLeadText, setQuickLeadText] = useState('');
  const [type, setType] = useState(Object.keys(types || {})[0] || 'HOA');
  const [notes, setNotes] = useState('');
  const [initialLeadDate, setInitialLeadDate] = useState(() => {
    const iso = new Date().toISOString().slice(0, 10);
    return iso;
  });
  const [numberOfUnits, setNumberOfUnits] = useState('');

  const [leadStatus, setLeadStatus] = useState('New');
  const [leadSource, setLeadSource] = useState('APM');

  // Warning dialog for suspicious names
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  // Use the provided save handler or a safe fallback to avoid circular imports
  const saveHandler =
    saveAssociationToDb ||
    (async () => {
      console.warn('No saveAssociationToDb handler provided to AssociationForm');
    });

  // access existing associations from context to prevent duplicates
  const { associations: existingAssociations = [] } = useContext(AssociationsContext) || {};

  const [engagementStrategy, setEngagementStrategy] = useState('Pursue');
  const normalizedName = useMemo(() => (name || '').toString().trim().toLowerCase(), [name]);

  const isDuplicate = useMemo(() => {
    if (!normalizedName) return false;
    return existingAssociations.some(
      a => (a.name || a.associationName || '').toString().trim().toLowerCase() === normalizedName
    );
  }, [normalizedName, existingAssociations]);

  const handleSave = async (bypassWarning = false) => {
    if (!name || name.trim().length < 3) return;

    // Compose the association object for validation
    const parsed = parseAddress(address);
    const values = {
      name: name.trim(),
      address: address.trim() || '',
      contactName: contactName.trim() || undefined,
      contactEmail: contactEmail.trim() || undefined,
      contactRole: contactRole.trim() || undefined,
      phone: phone.trim() || '',
      type: type || 'HOA',
      status: leadStatus || 'New',
      leadStatus: leadStatus || 'New',
      leadSource: leadSource || 'APM',
      engagementStrategy: engagementStrategy || 'Pursue',
      notes: notes ? notes.trim() : undefined,
      initialLeadDate: initialLeadDate
        ? new Date(initialLeadDate).toISOString()
        : new Date().toISOString(),
      numberOfUnits: numberOfUnits ? parseInt(numberOfUnits, 10) : undefined,
      // Location fields for grid display
      city: parsed.city || undefined,
      state: parsed.state || undefined,
      // include parsed address fields for downstream reports/automation
      _parsedCity: parsed.city,
      _parsedState: parsed.state,
      _parsedPostalCode: parsed.postalCode,
      _parsedStreet: parsed.street,
    };

    // Check for stubs using centralized validator
    const stubCheck = AssociationValidator.businessRules.validateStub(values);
    if (!stubCheck.valid && !bypassWarning) {
      setWarningMessage(stubCheck.message);
      setShowWarningDialog(true);
      return;
    }

    setSaving(true);
    try {
      const res = await saveHandler(values);
      // If the handler didn't return a truthy response, assume failure and keep the dialog open
      if (!res) {
        console.warn('Association save did not return a success response');
        setSaving(false);
        return;
      }
      setOpen(false);
      setName('');
    } catch (e) {
      console.error('Error saving association from minimal form', e);
      // don't close the dialog so user can retry
    } finally {
      setSaving(false);
    }
  };

  // Reset all form fields each time dialog opens so users always start with a fresh form
  useEffect(() => {
    if (!open) return;
    setQuickLeadText('');
    setName('');
    setAddress('');
    setPhone('');
    setContactName('');
    setContactEmail('');
    setContactRole('');
    setNotes('');
    setNumberOfUnits('');
    setInitialLeadDate(() => new Date().toISOString().slice(0, 10));
    setType(Object.keys(types || {})[0] || 'HOA');
    setLeadStatus('New');
    setLeadSource('APM');
    setEngagementStrategy('Pursue');
    // also clear any saving flag
    setSaving(false);
  }, [open]);

  // Use the pure parser util to map pasted lead text into fields
  const applyParsedLead = raw => {
    const p = parseLeadUtil(raw || '');
    if (!p) return;
    if (p.name) setName(p.name);
    if (p.contactName) setContactName(p.contactName);
    if (p.contactEmail) setContactEmail(p.contactEmail);
    if (p.contactRole) setContactRole(p.contactRole);
    if (p.phone) setPhone(p.phone);
    if (p.address) setAddress(p.address);
    if (p.notes) setNotes(p.notes);
    if (p.numberOfUnits) setNumberOfUnits(String(p.numberOfUnits));
    if (p.type) setType(p.type);
    setLeadStatus('New');
    setLeadSource('APM');
    setEngagementStrategy('Pursue');
  };

  return (
    <Box>
      <Tooltip title='Add Association'>
        <IconButton 
          color='primary' 
          onClick={() => setOpen(true)}
          sx={{ 
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
          }}
        >
          <Add />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth='md'
        PaperProps={{ sx: { minWidth: 520, maxWidth: '90vw', p: 0 } }}
      >
        <DialogTitle>Add Association</DialogTitle>
        <DialogContent>
          {/* Quick Lead Import */}
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mb: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ fontWeight: 'bold', mb: 1 }}>Quick Lead Import</Box>
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Paste your lead data here and it will be parsed automatically
            </Box>
            <TextField
              multiline
              minRows={4}
              fullWidth
              variant='outlined'
              value={quickLeadText}
              onChange={e => {
                const v = e.target.value;
                setQuickLeadText(v);
                try {
                  applyParsedLead(v);
                } catch {
                  /* ignore parse errors while typing */
                }
              }}
              onPaste={e => {
                try {
                  e.preventDefault(); // Prevent default paste to avoid double-pasting
                  const pasted = (e.clipboardData && e.clipboardData.getData('text')) || '';
                  if (pasted.trim()) {
                    setQuickLeadText(pasted); // Replace content instead of combining
                    // parse right away
                    applyParsedLead(pasted);
                  }
                } catch {
                  // ignore paste parsing errors
                }
              }}
              placeholder={
                'Association: Lakeview Community\nFirst Name: Mark\nLast Name: Dempsey\nEmail: m.dempsey@gmail.com\nPhone: 2065240783\nProperty Street: 19828 Military Rd S\nProperty City: SEATTLE\nUnits: 48\nNote: Board prefers email contact'
              }
            />
          </Box>

          <TextField
            autoFocus
            margin='dense'
            variant='outlined'
            label='Association Name'
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
          />
          {isDuplicate && (
            <Alert severity='warning' sx={{ mt: 1 }}>
              An association with this name already exists — save is disabled to prevent duplicates.
            </Alert>
          )}

          <TextField
            margin='dense'
            variant='outlined'
            label='Contact Name'
            fullWidth
            value={contactName}
            onChange={e => setContactName(e.target.value)}
          />

          <TextField
            margin='dense'
            variant='outlined'
            label='Contact Email'
            fullWidth
            value={contactEmail}
            onChange={e => setContactEmail(e.target.value)}
          />

          <TextField
            margin='dense'
            variant='outlined'
            label='Contact Role'
            fullWidth
            select
            value={contactRole}
            onChange={e => setContactRole(e.target.value)}
            helperText="Contact's role in the association"
          >
            <MenuItem value=''>Select Role</MenuItem>
            <MenuItem value='Board President'>Board President</MenuItem>
            <MenuItem value='Board Vice President'>Board Vice President</MenuItem>
            <MenuItem value='Board Secretary'>Board Secretary</MenuItem>
            <MenuItem value='Board Treasurer'>Board Treasurer</MenuItem>
            <MenuItem value='Board Member'>Board Member</MenuItem>
            <MenuItem value='Committee Member'>Committee Member</MenuItem>
            <MenuItem value='Property Manager'>Property Manager</MenuItem>
            <MenuItem value='Assistant Manager'>Assistant Manager</MenuItem>
            <MenuItem value='Maintenance Supervisor'>Maintenance Supervisor</MenuItem>
            <MenuItem value='HOA Manager'>HOA Manager</MenuItem>
            <MenuItem value='Community Manager'>Community Manager</MenuItem>
            <MenuItem value='Owner'>Owner</MenuItem>
            <MenuItem value='Resident'>Resident</MenuItem>
            <MenuItem value='Other'>Other</MenuItem>
          </TextField>

          <TextField
            margin='dense'
            variant='outlined'
            label='Address'
            fullWidth
            value={address}
            onChange={e => setAddress(e.target.value)}
            helperText={
              address
                ? (() => {
                    const p = parseAddress(address);
                    const parts = [];
                    if (p.city) parts.push(p.city);
                    if (p.state) parts.push(p.state);
                    if (p.postalCode) parts.push(p.postalCode);
                    return parts.length ? `Parsed: ${parts.join(', ')}` : '';
                  })()
                : ''
            }
          />

          <TextField
            margin='dense'
            variant='outlined'
            label='Phone'
            fullWidth
            value={phone}
            onChange={e => setPhone(e.target.value)}
            helperText={phone ? formatPhone(phone) : ''}
          />
          {/* Website removed as requested */}

          <TextField
            select
            margin='dense'
            variant='outlined'
            label='Type'
            fullWidth
            value={type}
            onChange={e => setType(e.target.value)}
          >
            {Object.keys(types || {}).map(key => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            margin='dense'
            variant='outlined'
            label='Initial Lead Date'
            type='date'
            fullWidth
            value={initialLeadDate}
            onChange={e => setInitialLeadDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            margin='dense'
            variant='outlined'
            label='Number of Units'
            type='number'
            fullWidth
            value={numberOfUnits}
            onChange={e => setNumberOfUnits(e.target.value)}
          />

          <TextField
            margin='dense'
            variant='outlined'
            label='Notes'
            fullWidth
            multiline
            minRows={3}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={isDuplicate || !name || name.trim().length < 3}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog for Suspicious Names */}
      <Dialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color='error' />
          Data Quality Warning
        </DialogTitle>
        <DialogContent>
          <Alert severity='error' sx={{ mb: 2 }}>
            <strong>Potential Data Entry Error Detected</strong>
          </Alert>
          <Box sx={{ whiteSpace: 'pre-line', mb: 2 }}>{warningMessage}</Box>
          <Alert severity='info'>
            <strong>Recommended Actions:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Put the person's name in the "Contact Name" field</li>
              <li>Put the actual property/association name in the "Association Name" field</li>
              <li>Examples: "Sunset Ridge HOA", "Maple Grove Condominiums", "Parkside Community"</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWarningDialog(false)}>Go Back & Fix</Button>
          <Button
            variant='outlined'
            color='warning'
            onClick={() => {
              setShowWarningDialog(false);
              handleSave(true); // Bypass warning
            }}
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
